/**
 * ObstructionsPanelContent Component
 *
 * Obstructions side panel: wires the obstruction store/filters into the shared
 * `ArtifactListPanelContent` shell and supplies the obstruction list.
 *
 * Atomic Zustand subscriptions only — list items use `useShallow`.
 */
import { memo, useCallback, useEffect } from 'react';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore } from '@/store';
import {
  selectObstructionsHasNextPage,
  selectObstructionsHasLoaded,
  selectObstructionsItems,
  selectObstructionsLoading,
  selectObstructionsSelectedId,
  selectObstructionsTotalCount,
} from '@/store/selectors/obstructionSelectors';
import {
  selectHasActiveObstructionFiltersInMapPanel,
  selectObstructionFilterKey,
} from '@/store/selectors/obstructionFilterSelectors';
import { selectCurrentWorkspaceId } from '@/store/selectors/authSelectors';
import { PaginatedList } from '@/components/ui/PaginatedList';
import { EmptyState } from '@/components/common/EmptyState';
import { ObstructionCard } from '@/components/obstructions/ObstructionCard';
import {
  ObstructionFilters,
  ObstructionFilterPills,
} from '@/components/obstructions/ObstructionFilters';
import { ICON_SIZE_EMPTY_STATE } from '@/constants/ui';
import type { ObstructionListItem } from '@/types/obstructions';
import { ArtifactListPanelContent } from './ArtifactListPanelContent';
import styles from './SidePanel.module.css';

interface ObstructionsPanelContentProps {
  readonly onClose: () => void;
}

export const ObstructionsPanelContent = memo(function ObstructionsPanelContent({
  onClose,
}: ObstructionsPanelContentProps) {
  const { t } = useTranslation();

  // Atomic state subscriptions
  const obstructions = useGeoMarkupStore(useShallow(selectObstructionsItems));
  const isLoading = useGeoMarkupStore(selectObstructionsLoading);
  const hasLoaded = useGeoMarkupStore(selectObstructionsHasLoaded);
  const selectedId = useGeoMarkupStore(selectObstructionsSelectedId);
  const hasNextPage = useGeoMarkupStore(selectObstructionsHasNextPage);
  const totalCount = useGeoMarkupStore(selectObstructionsTotalCount);
  const hasActiveFilters = useGeoMarkupStore(
    selectHasActiveObstructionFiltersInMapPanel
  );
  const filterKey = useGeoMarkupStore(selectObstructionFilterKey);
  const currentWorkspaceId = useGeoMarkupStore(selectCurrentWorkspaceId);

  // Action subscriptions (stable refs → zero re-renders)
  const fetchObstructions = useGeoMarkupStore(
    (state) => state.obstructions.fetchObstructions
  );
  const loadMore = useGeoMarkupStore((state) => state.obstructions.loadMore);
  const startObstructionLocationSelection = useGeoMarkupStore(
    (state) => state.ui.startObstructionLocationSelection
  );
  const search = useGeoMarkupStore((state) => state.obstructionFilters.search);
  const setSearch = useGeoMarkupStore(
    (state) => state.obstructionFilters.setSearch
  );
  const isObstructionFormOpen = useGeoMarkupStore(
    (state) => state.obstructions.formIsOpen
  );
  const isSelectingObstructionLocation = useGeoMarkupStore(
    (state) => state.ui.isSelectingObstructionLocation
  );
  const isAddObstructionDisabled =
    isObstructionFormOpen || isSelectingObstructionLocation;

  // Refetch on workspace OR filter change (paginated-list Rule 2).
  useEffect(() => {
    if (currentWorkspaceId) {
      fetchObstructions();
    }
  }, [currentWorkspaceId, filterKey, fetchObstructions]);

  const handleNewObstruction = useCallback(() => {
    // The form opens only after the user clicks an anchor on the map
    // (see MapContainer → useMapEventHandlers → createObstructionAt).
    startObstructionLocationSelection();
  }, [startObstructionLocationSelection]);

  return (
    <ArtifactListPanelContent
      onClose={onClose}
      search={{
        placeholder: t('obstructions.panel.search'),
        value: search,
        onChange: setSearch,
      }}
      filters={{
        hasActiveFilters,
        label: t('filters.title'),
        pills: <ObstructionFilterPills />,
        panel: <ObstructionFilters />,
      }}
      header={{
        title: t('obstructions.title'),
        count: totalCount,
        addLabel: t('obstructions.panel.newObstruction'),
        onAdd: handleNewObstruction,
        addDisabled: isAddObstructionDisabled,
      }}
    >
      <div className={styles.list}>
        <PaginatedList<ObstructionListItem>
          data={obstructions}
          renderItem={(obstruction) => (
            <ObstructionCard
              obstruction={obstruction}
              isSelected={obstruction.id === selectedId}
            />
          )}
          keyExtractor={(obstruction) => obstruction.id}
          hasNextPage={hasNextPage}
          onLoadMore={loadMore}
          isLoading={isLoading}
          hasLoaded={hasLoaded}
          stateRenderers={{
            empty: () => (
              <EmptyState
                icon={
                  <IconAlertTriangle
                    size={ICON_SIZE_EMPTY_STATE}
                    stroke={1.5}
                  />
                }
                title={t('obstructions.empty.title')}
                description={t('obstructions.empty.description')}
              />
            ),
          }}
          styling={{ gap: 'xs' }}
          aria-label={t('obstructions.title')}
        />
      </div>
    </ArtifactListPanelContent>
  );
});
